import { secondsToTimeString } from '@/helpers/dateHelper';
import { ShiftDetailsForDay } from '@/services/employee/useShiftDetailsForDay';
import { useGetTaskIcon, useTasks } from '@/services/tasks/useTasks';
import { useMemo } from 'react';
import { IconType } from 'react-icons';
import { BsCupFill } from 'react-icons/bs';
import strings from '../ess.translations';

interface TimelineItemBase {
  label: string;
  icon?: IconType;
  bgColor?: string;
  textColor?: string;
}

export type TimelineItem =
  | (TimelineItemBase & { type: 'shiftStart' | 'shiftEnd' | 'locationOpen' | 'locationClose'; time: number })
  | (TimelineItemBase & { type: 'break' | 'task'; start: number; end: number });

const getSortTime = (item: TimelineItem) => ('time' in item ? item.time : item.start);

export const formatTimelineTime = (item: TimelineItem) =>
  'time' in item
    ? secondsToTimeString(item.time)
    : `${secondsToTimeString(item.start)} - ${secondsToTimeString(item.end)}`;

export const useBuildShiftTimeline = (data?: ShiftDetailsForDay): TimelineItem[] => {
  const getTaskIcon = useGetTaskIcon();
  const tasks = useTasks();

  return useMemo(() => {
    if (!data) return [];

    const shiftStart: TimelineItem = {
      time: data.start,
      type: 'shiftStart',
      label: strings.viewShiftDetails.shiftStart
    };

    const shiftEnd: TimelineItem = {
      time: data.end,
      type: 'shiftEnd',
      label: strings.viewShiftDetails.shiftEnd
    };

    const breaks: TimelineItem[] = (data.breaks ?? []).map(b => ({
      start: b.start,
      end: b.end,
      type: 'break',
      label: strings.viewShiftDetails.breakLabel,
      bgColor: 'rgba(126, 178, 221, 1)',
      textColor: '#FFF',
      icon: BsCupFill
    }));

    const taskItems: TimelineItem[] = (data.timeSlots ?? []).flatMap(slot => {
      if (!slot.properties) return [];

      try {
        const parsed = JSON.parse(slot.properties);
        if (parsed.type !== 'task') return [];

        const lookupTask = parsed.context ? tasks.find(t => t.id === parsed.context) : undefined;

        if (!lookupTask) return [];

        return [
          {
            start: slot.start,
            end: slot.end,
            type: 'task',
            label: lookupTask.displayName ?? lookupTask.name,
            icon: getTaskIcon(lookupTask.id),
            bgColor: lookupTask.color,
            textColor: lookupTask.textColor
          }
        ];
      } catch {
        return [];
      }
    });

    const locationHoursItems: TimelineItem[] = [];

    if (typeof data.openTime === 'number' && data.openTime <= data.end && data.openTime >= data.start) {
      locationHoursItems.push({
        time: data.openTime,
        type: 'locationOpen',
        label: strings.viewShiftDetails.storeOpens
      });
    }

    if (typeof data.closeTime === 'number' && data.closeTime <= data.end && data.closeTime >= data.start) {
      locationHoursItems.push({
        time: data.closeTime,
        type: 'locationClose',
        label: strings.viewShiftDetails.storeCloses
      });
    }

    return [shiftStart, shiftEnd, ...breaks, ...taskItems, ...locationHoursItems].sort(
      (a, b) => getSortTime(a) - getSortTime(b)
    );
  }, [data, tasks, getTaskIcon]);
};
